import type { ReactNode } from 'react'
import type { CampaignStatus } from '@/lib/campaigns'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

const SAVE_SUCCESS_FEEDBACK_MS = 2000

type CampaignStatusControl = {
  value: CampaignStatus
  onChange: (status: CampaignStatus) => void
}

export type CampanhasLayoutContextValue = {
  /** Função chamada ao clicar em "Salvar campanha" no header ou no botão fixo. */
  saveTrigger: (() => void) | null
  setSaveTrigger: (fn: (() => void) | null) => void
  /** Indica se o formulário está salvando (desabilita o botão do header). */
  isSubmitting: boolean
  setIsSubmitting: (value: boolean) => void
  hasUnsavedChanges: boolean
  setHasUnsavedChanges: (value: boolean) => void
  isSaveSuccessVisible: boolean
  reportSaveSuccess: () => void
  /** Destino do botão "Visualizar Página" na barra fixa da edição. */
  viewPageUrl: string | null
  setViewPageUrl: (url: string | null) => void
  activeEditTab: string | null
  setActiveEditTab: (tab: string | null) => void
  campaignStatusControl: CampaignStatusControl | null
  setCampaignStatusControl: (control: CampaignStatusControl | null) => void
}

const CampanhasLayoutContext = createContext<CampanhasLayoutContextValue | undefined>(
  undefined
)

export function CampanhasLayoutProvider({ children }: { children: ReactNode }) {
  const [saveTrigger, setSaveTriggerState] = useState<(() => void) | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [saveSuccessUntil, setSaveSuccessUntil] = useState<number | null>(null)
  const [isSaveSuccessVisible, setIsSaveSuccessVisible] = useState(false)
  const [viewPageUrl, setViewPageUrl] = useState<string | null>(null)
  const [activeEditTab, setActiveEditTab] = useState<string | null>(null)
  const [campaignStatusControl, setCampaignStatusControl] =
    useState<CampaignStatusControl | null>(null)

  const setSaveTrigger = useCallback((fn: (() => void) | null) => {
    setSaveTriggerState(() => fn)
  }, [])

  const reportSaveSuccess = useCallback(() => {
    setSaveSuccessUntil(Date.now() + SAVE_SUCCESS_FEEDBACK_MS)
    setIsSaveSuccessVisible(true)
  }, [])

  useEffect(() => {
    if (!saveSuccessUntil) return

    const remainingMs = saveSuccessUntil - Date.now()
    if (remainingMs <= 0) {
      queueMicrotask(() => {
        setIsSaveSuccessVisible(false)
        setSaveSuccessUntil(null)
      })
      return
    }

    const timer = window.setTimeout(() => {
      setIsSaveSuccessVisible(false)
      setSaveSuccessUntil(null)
    }, remainingMs)

    return () => window.clearTimeout(timer)
  }, [saveSuccessUntil])

  const value = useMemo(
    () => ({
      saveTrigger,
      setSaveTrigger,
      isSubmitting,
      setIsSubmitting,
      hasUnsavedChanges,
      setHasUnsavedChanges,
      isSaveSuccessVisible,
      reportSaveSuccess,
      viewPageUrl,
      setViewPageUrl,
      activeEditTab,
      setActiveEditTab,
      campaignStatusControl,
      setCampaignStatusControl,
    }),
    [
      activeEditTab,
      campaignStatusControl,
      hasUnsavedChanges,
      isSaveSuccessVisible,
      isSubmitting,
      reportSaveSuccess,
      saveTrigger,
      setSaveTrigger,
      viewPageUrl,
    ]
  )

  return (
    <CampanhasLayoutContext.Provider value={value}>
      {children}
    </CampanhasLayoutContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCampanhasLayout(): CampanhasLayoutContextValue {
  const ctx = useContext(CampanhasLayoutContext)
  if (ctx === undefined) {
    throw new Error('useCampanhasLayout must be used within CampanhasLayoutProvider')
  }
  return ctx
}
